import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import allStepsInfoRaw from "../../pages/dashboard/config/dashboardStepsInfo";
import { isDetailedOrderData } from "../../helpers/uiConditions";

const allStepsInfo = allStepsInfoRaw as CashBoxStepInfo[];

const initialState: StepManagementState = {
    currentStepId: allStepsInfo[0]?.id,
    stepsData: [allStepsInfo[0]],
    tablesList: undefined,
    currentOrderType: undefined,
    currentTableId: undefined,
    currentTableTitle: "",
    currentOrderId: undefined,
    currentOrderDetails: null,
    currentOrderDetailsErrorText: "",
    currentTableOrdersList: null,
    currentTableOlderOrdersList: [],
    totalPriceOfOrderAddedItems: 0,
    tablesListItemsAreLoading: undefined,
    ordersListItemsAreLoading: false,
    olderOrdersListItemsAreLoading: false,
    ordersListBackgroundLoading: false,
    loadingForCreatingNewOrder: false,
    orderAddedItemsLoading: false,
    edibleCategories: undefined,
    edibleCategoriesAreLoading: undefined,
    selectedCategoryIdOrType: undefined,
    availableItemsAreLoading: false,
    ediblesListLastScrollPosition: undefined,
    availableItemsList: null,
    itemPreviewBoxIsLoading: false,
    previewItemDetails: null,
    previewCustomItemStepsData: null,
    edibleTypeForPreviewOrEdit: undefined,
    initialItemCustomizationForEditing: null,
    manualItemModalShowState: false,
    showSendTokitchenModal: false,
    showChangeTableModal: false,
    itemsForSendToKitchen: [],
    customItemsForSendToKitchen: [],
    manualItemsForSendToKitchen: [],
    allItemsAreSelectedForSendingToKitchen: false,
    showTransactionsHistoryModal: false,
    showAcceptOrderModal: false,
    showOrderDetailsModal: false,
};

const stepManagementSlice = createSlice({
    name: "stepManagement",
    initialState,
    reducers: {
        selectStep: (state, action: PayloadAction<{ targetStepId?: string } | undefined>) => {
            const { targetStepId } = action.payload || {};

            const targetStepData = allStepsInfo.find((stp) => stp.id === targetStepId);
            const targetStepIndex = allStepsInfo.indexOf(targetStepData as CashBoxStepInfo);
            const new_stepsData = allStepsInfo.filter((stp, index) => index <= targetStepIndex);

            state.currentStepId = targetStepData?.id || "";
            state.stepsData = new_stepsData;
        },
        setTableAndOrderTypesListData: (state, action: PayloadAction<{ tablesList: CashBoxTableOrOrderTypeItem[] } | undefined>) => {
            const { tablesList } = action.payload || {};

            state.tablesList = tablesList;
            if (tablesList) {
                state.tablesListItemsAreLoading = false;
            }
        },
        setSelectedTableOrOrderTypeInfo: (state, action: PayloadAction<{
            tableId?: number | null;
            tableTitle?: string | null;
            orderType?: ApiOrderType | null;
        } | undefined>) => {
            const { tableId, tableTitle, orderType } = action.payload || {};

            state.currentTableId = tableId ?? null;
            state.currentTableTitle = tableTitle || "";
            state.currentOrderType = orderType ?? null;
        },
        setTableOrdersListData: (state, action: PayloadAction<{
            tableOrdersList?: (CashBoxOrderSummary | CashBoxOrderDetails)[] | null;
            tableOlderOrdersList?: (CashBoxOrderSummary | CashBoxOrderDetails)[] | null;
        } | undefined>) => {
            const { tableOrdersList, tableOlderOrdersList } = action.payload || {};

            if (tableOrdersList !== undefined) {
                state.currentTableOrdersList = tableOrdersList;
                state.ordersListItemsAreLoading = false;
            }

            if (tableOlderOrdersList !== undefined) {
                state.currentTableOlderOrdersList = (tableOlderOrdersList || []).filter(oldOrder => (
                    !state.currentTableOrdersList?.find(ord => ord.id === oldOrder.id)
                ));
                state.olderOrdersListItemsAreLoading = false;
            }

            // state.currentOrderDetails =
            //     (state.currentTableOrdersList || []).find(ord => ord.id === currentOrderId) ||
            //     (state.currentTableOlderOrdersList || []).find(ord => ord.id === currentOrderId) ||
            //     {};
            // state.currentOrderDetailsErrorText = "";
        },
        addToTableOrders: (state, action: PayloadAction<{
            orderDetails?: CashBoxOrderDetails | null;
            isSelectedOrder?: boolean;
        } | undefined>) => {
            const { orderDetails, isSelectedOrder } = action.payload || {};

            if (!orderDetails) return;

            state.currentTableOrdersList = [
                ...(state.currentTableOrdersList || []),
                orderDetails,
            ];

            if (isSelectedOrder) {
                state.currentOrderId = orderDetails.id;
                state.currentOrderDetails = orderDetails;
                state.currentOrderDetailsErrorText = "";
            }
        },
        updateTableOrder: (state, action: PayloadAction<{
            orderDetails?: CashBoxOrderSummary | CashBoxOrderDetails | null;
            isSelectedOrder?: boolean;
        } | undefined>) => {
            const { orderDetails, isSelectedOrder } = action.payload || {};

            if (!orderDetails) return;

            const isInHistoryList = (state.currentTableOlderOrdersList || []).find(o => o.id === orderDetails?.id);
            const isInMainList =
                !isInHistoryList &&
                (state.currentTableOrdersList || []).find(o => o.id === orderDetails?.id);

            if (isInHistoryList) {
                state.currentTableOlderOrdersList = (state.currentTableOlderOrdersList || [])
                    .map(o => o.id === orderDetails?.id ? orderDetails : o);
            }
            else if (isInMainList) {
                state.currentTableOrdersList = (state.currentTableOrdersList || [])
                    .map(o => o.id === orderDetails?.id ? orderDetails : o);
            }

            // if (isSelectedOrder && (isInHistoryList || isInMainList)) {
            //     state.currentOrderId = orderDetails.id;
            //     state.currentOrderDetails = orderDetails;
            //     state.currentOrderDetailsErrorText = "";
            // }

            state.currentOrderDetails = orderDetails;
            state.currentOrderDetailsErrorText = "";

            if (isSelectedOrder) {
                state.currentOrderId = orderDetails.id;
            }
        },
        setSelectedCategoryIdOrType: (state, action: PayloadAction<{ selectedCategoryIdOrType: number | string | string | undefined }>) => {
            const { selectedCategoryIdOrType } = action.payload || {};
            state.selectedCategoryIdOrType = selectedCategoryIdOrType;
        },
        setSelectedOrder: (state, action: PayloadAction<{ orderId?: number | null } | undefined>) => {
            const { orderId = null } = action.payload || {};

            state.currentOrderId = orderId;
            state.currentOrderDetails =
                (state.currentTableOrdersList || []).find(ord => ord.id === orderId) ||
                (state.currentTableOlderOrdersList || []).find(ord => ord.id === orderId) ||
                null;
            state.currentOrderDetailsErrorText = "";
        },
        changeCurrentOrderData: (state, action: PayloadAction<{
            currentOrderDetails?: CashBoxOrderItemData | null;
            errorText?: string;
        } | undefined>) => {
            const {
                currentOrderDetails,
                errorText = "",
            } = action.payload || {};

            state.currentOrderId = currentOrderDetails?.id || null;
            state.currentOrderDetails = currentOrderDetails || null;
            state.currentOrderDetailsErrorText = errorText;
        },
        changeTotalPriceOfOrderAddedItems: (state, action: PayloadAction<{ totalPrice?: number } | undefined>) => {
            const { totalPrice } = action.payload || {};
            if (totalPrice === undefined) return;
            state.totalPriceOfOrderAddedItems = totalPrice;
        },
        changeCurrentEditingData: (state, action: PayloadAction<{
            edibleTypeForPreviewOrEdit?: Exclude<EdibleGeneralType, "manual"> | null;
            initialItemCustomizationForEditing?: CashBoxOrderItem | CashBoxCustomOrderItem | null;
        } | undefined>) => {
            const {
                edibleTypeForPreviewOrEdit,
                initialItemCustomizationForEditing,
            } = action.payload || {};

            state.edibleTypeForPreviewOrEdit = edibleTypeForPreviewOrEdit;
            state.initialItemCustomizationForEditing = initialItemCustomizationForEditing ?? null;
        },
        setManualItemModalState: (state, action: PayloadAction<{ show?: boolean | FormModalShowState<CashBoxManualOrderItem> } | undefined>) => {
            const { show } = action.payload || {};
            state.manualItemModalShowState = show ?? false;
        },
        openSendToKitchenModal: (state) => {
            state.showSendTokitchenModal = true;
        },
        closeSendToKitchenModal: (state) => {
            state.showSendTokitchenModal = false;
        },
        openChangeTableModal: (state) => {
            state.showChangeTableModal = true;
        },
        closeChangeTableModal: (state) => {
            state.showChangeTableModal = false;
        },
        openAcceptOrderModal: (state) => {
            state.showAcceptOrderModal = true;
        },
        closeAcceptOrderModal: (state) => {
            state.showAcceptOrderModal = false;
        },
        openOrderDetailsModal: (state) => {
            state.showOrderDetailsModal = true;
        },
        closeOrderDetailsModal: (state) => {
            state.showOrderDetailsModal = false;
        },
        updateItemsForSendToKitchen: (state, action: PayloadAction<{
            id?: CashBoxId;
            addToSelected?: boolean;
            itemType?: EdibleGeneralType;
        } | undefined>) => {
            const { id, addToSelected, itemType = "normal" } = action.payload || {};

            if (itemType === "custom") {
                if (addToSelected) {
                    state.customItemsForSendToKitchen.push({ id });
                } else {
                    state.customItemsForSendToKitchen =
                        state.customItemsForSendToKitchen.filter((it) => it.id !== id);
                }
            }
            else if (itemType === "manual") {
                if (addToSelected) {
                    state.manualItemsForSendToKitchen.push({ id });
                } else {
                    state.manualItemsForSendToKitchen =
                        state.manualItemsForSendToKitchen.filter((it) => it.id !== id);
                }
            }
            else {
                if (addToSelected) {
                    state.itemsForSendToKitchen.push({ id });
                } else {
                    state.itemsForSendToKitchen = state.itemsForSendToKitchen.filter(it => it.id !== id);
                }
            }

            const currentOrderDetails = isDetailedOrderData(state.currentOrderDetails)
                ? state.currentOrderDetails as CashBoxOrderDetails
                : null;

            let allItemsAreAddedForSendToKitchen = true;
            currentOrderDetails?.items?.length && currentOrderDetails.items.forEach(food => {
                if (allItemsAreAddedForSendToKitchen && !food.is_sent_to_kitchen) {
                    const itemIsAdded = state.itemsForSendToKitchen?.find(it => it.id === food.id) !== undefined;

                    if (!itemIsAdded) {
                        allItemsAreAddedForSendToKitchen = false;
                    }
                }
            });
            currentOrderDetails?.customItems?.length && currentOrderDetails.customItems.forEach(cust_it => {
                if (allItemsAreAddedForSendToKitchen && !cust_it.is_sent_to_kitchen) {
                    const itemIsAdded = state.customItemsForSendToKitchen?.find(it => it.id === cust_it.id) !== undefined;

                    if (!itemIsAdded) {
                        allItemsAreAddedForSendToKitchen = false;
                    }
                }
            });
            currentOrderDetails?.freeItems?.length && currentOrderDetails.freeItems.forEach(man_it => {
                if (allItemsAreAddedForSendToKitchen && !man_it.is_sent_to_kitchen) {
                    const itemIsAdded = state.manualItemsForSendToKitchen?.find(it => it.id === man_it.id) !== undefined;

                    if (!itemIsAdded) {
                        allItemsAreAddedForSendToKitchen = false;
                    }
                }
            });

            state.allItemsAreSelectedForSendingToKitchen = allItemsAreAddedForSendToKitchen;
        },
        toggleSelectAllItemsForSendingToKitchen: (state, action: PayloadAction<{ selectAll?: boolean } | undefined>) => {
            const { selectAll = !state.allItemsAreSelectedForSendingToKitchen } = action.payload || {};
            const currentOrderDetails = isDetailedOrderData(state.currentOrderDetails)
                ? state.currentOrderDetails as CashBoxOrderDetails
                : null;

            if (selectAll) {
                let items: Array<{ id?: CashBoxId }> = currentOrderDetails?.items?.length
                    ? currentOrderDetails.items
                        .filter(it => !it.is_sent_to_kitchen)
                        .map(it => ({ id: it.id }))
                    : [];

                let customItems: Array<{ id?: CashBoxId }> = currentOrderDetails?.customItems?.length
                    ? currentOrderDetails.customItems
                        .filter(it => !it.is_sent_to_kitchen)
                        .map(it => ({ id: it.id }))
                    : [];

                let manualItems: Array<{ id?: CashBoxId }> = currentOrderDetails?.freeItems?.length
                    ? currentOrderDetails.freeItems
                        .filter(it => !it.is_sent_to_kitchen)
                        .map(it => ({ id: it.id }))
                    : [];

                state.itemsForSendToKitchen = items;
                state.customItemsForSendToKitchen = customItems;
                state.manualItemsForSendToKitchen = manualItems;
                state.allItemsAreSelectedForSendingToKitchen = true;
            }
            else {
                state.itemsForSendToKitchen = [];
                state.customItemsForSendToKitchen = [];
                state.manualItemsForSendToKitchen = [];
                state.allItemsAreSelectedForSendingToKitchen = false;
            }
        },
        changeCheckoutDetailsOfCurrentOrder: (state, action: PayloadAction<{ new_checkoutDetails?: TransactionCheckoutUpdatePayload } | undefined>) => {
            const { new_checkoutDetails } = action.payload || {};
            if (!isDetailedOrderData(state.currentOrderDetails) || !new_checkoutDetails?.order_pay?.checkout_id) {
                return;
            }
            const target_checkoutId = new_checkoutDetails?.order_pay?.checkout_id;
            const currentOrderDetails = state.currentOrderDetails as CashBoxOrderDetails;
            const checkouts_clone = [...currentOrderDetails.checkouts];

            const new_checkouts_listData = Array.isArray(checkouts_clone) && checkouts_clone.map((checkoutData) => {
                const is_target_checkout = checkoutData.checkout_id === target_checkoutId;

                const new_checkoutData = { ...checkoutData };
                if (is_target_checkout) {
                    if (new_checkoutDetails?.order_pay?.payment_status) {
                        new_checkoutData.payment_status = new_checkoutDetails.order_pay?.payment_status;
                    }
                    if (new_checkoutDetails?.order_pay?.receipt_temp_link) {
                        new_checkoutData.receipt_temp_link = new_checkoutDetails.order_pay?.receipt_temp_link;
                    }
                    if (new_checkoutDetails?.order_pay?.updated_at) {
                        new_checkoutData.updated_at = new_checkoutDetails.order_pay?.updated_at;
                    }

                    const new_taxesData = new_checkoutDetails?.order_pay?.taxes;
                    if (new_taxesData?.length) {
                        new_checkoutData.taxes = new_taxesData;
                    }
                }
                return new_checkoutData;
            });

            state.currentOrderDetails = {
                ...currentOrderDetails,
                checkouts: new_checkouts_listData || [],
            };
        },
        toggleTransactionsHistoryModal: (state, action: PayloadAction<{ showModal?: boolean } | undefined>) => {
            const { showModal = !state.showTransactionsHistoryModal } = action.payload || {};
            state.showTransactionsHistoryModal = showModal;
        },
        setEdibleCategories: (state, action: PayloadAction<{ edibleCategories?: CashBoxEdibleDetailedCategory[] } | undefined>) => {
            const { edibleCategories } = action.payload || {};
            state.edibleCategories = edibleCategories;
        },
        setEdibleCategoriesLoading: (state, action: PayloadAction<boolean | undefined>) => {
            state.edibleCategoriesAreLoading = !!action.payload;
        },
        setEdiblesListLastScrollPosition: (state, action: PayloadAction<number>) => {
            state.ediblesListLastScrollPosition = action.payload;
        },
        setAvalableItems: (state, action: PayloadAction<{ availableItemsList?: CashBoxNormalFoodData[] | CashBoxCustomFoodData[] | null } | undefined>) => {
            const { availableItemsList } = action.payload || {};

            state.availableItemsList = availableItemsList ?? null;
            if (availableItemsList) {
                state.availableItemsAreLoading = false;
            }
        },
        setEdibleItemPreviewDetails: (state, action: PayloadAction<{
            previewItemDetails?: CashBoxNormalFoodDetailedData | null;
            previewCustomItemStepsData?: CashBoxCustomItemStep[] | null;
        } | undefined>) => {
            const { previewItemDetails, previewCustomItemStepsData } = action.payload || {};
            state.previewItemDetails = previewItemDetails ?? null;
            state.previewCustomItemStepsData = previewCustomItemStepsData ?? null;
        },
        setOrderAddedItemsLoading: (state, action: PayloadAction<boolean | undefined>) => {
            state.orderAddedItemsLoading = !!action.payload;
        },
        activateLoaderForTablesList: (state) => {
            state.tablesListItemsAreLoading = true;
        },
        activateLoaderForOrdersList: (state) => {
            state.ordersListItemsAreLoading = true;
        },
        activateLoaderForOlderOrdersList: (state) => {
            state.olderOrdersListItemsAreLoading = true;
        },
        setOrdersListBackgroundLoading: (state, action: PayloadAction<boolean | undefined>) => {
            state.ordersListBackgroundLoading = !!action.payload;
        },
        setCreatingNewOrderLoading: (state, action: PayloadAction<boolean | undefined>) => {
            state.loadingForCreatingNewOrder = !!action.payload;
        },
        activateLoaderForAvailableItemsList: (state) => {
            state.availableItemsAreLoading = true;
        },
        setItemPreviewBoxLoading: (state, action: PayloadAction<boolean | undefined>) => {
            state.itemPreviewBoxIsLoading = !!action.payload;
        },
    },
});

export const {
    selectStep,
    setTableAndOrderTypesListData,
    setSelectedTableOrOrderTypeInfo,
    setTableOrdersListData,
    addToTableOrders,
    updateTableOrder,
    setSelectedCategoryIdOrType,
    setSelectedOrder,
    changeCurrentOrderData,
    changeTotalPriceOfOrderAddedItems,
    changeCurrentEditingData,
    setManualItemModalState,
    openSendToKitchenModal,
    closeSendToKitchenModal,
    openChangeTableModal,
    closeChangeTableModal,
    updateItemsForSendToKitchen,
    toggleSelectAllItemsForSendingToKitchen,
    changeCheckoutDetailsOfCurrentOrder,
    toggleTransactionsHistoryModal,
    setEdibleCategories,
    setEdibleCategoriesLoading,
    setAvalableItems,
    setEdibleItemPreviewDetails,
    setOrderAddedItemsLoading,
    activateLoaderForTablesList,
    activateLoaderForOrdersList,
    activateLoaderForOlderOrdersList,
    setOrdersListBackgroundLoading,
    setCreatingNewOrderLoading,
    setEdiblesListLastScrollPosition,
    activateLoaderForAvailableItemsList,
    setItemPreviewBoxLoading,
    openAcceptOrderModal,
    closeAcceptOrderModal,
    openOrderDetailsModal,
    closeOrderDetailsModal,
} = stepManagementSlice.actions;

export default stepManagementSlice.reducer;
